import { Upload } from 'graphql-upload';
import { Readable } from 'stream';
import { Request as ExpressRequest } from 'express';
import { ContractLegalStatus, TemplateContract } from '../types.shared';
import { HttpError, HttpStatus } from '../error';

export enum EsignProviderErrorType {
  /** Le fournisseur a rejeté le payload envoyé (HTTP 4xx) : erreur de donnée, un retry échouera à l'identique. */
  ValidationError = 'ValidationError',
  /** Panne côté fournisseur (HTTP 5xx) : une nouvelle tentative peut réussir. */
  InternalError = 'InternalError',
}

/**
 * Erreur du fournisseur de signature électronique (même esprit que
 * `InvoiceProviderError` côté facturation). Étend `HttpError` pour rester
 * compatible avec les gestionnaires `instanceof HttpError` existants.
 */
export class EsignProviderError extends HttpError {
  constructor(
    message: string,
    public provider: string,
    public recoverableError: boolean,
    public type: EsignProviderErrorType,
    status: HttpStatus,
    meta?: unknown,
  ) {
    super(message, status, meta);
    Object.defineProperty(this, 'name', { value: 'EsignProviderError' });
  }
}

export type LegalContractWebhookHeaders = {
  legalContractId: string;
};

export type WebhookHeaders = LegalContractWebhookHeaders;

export type SignatureOptions = {
  signerEmail: string;
  signerFirstname: string;
  signerLastname: string;
  senderEmail?: string;
  documentName?: string;
  email?: {
    subject: string;
    message: string;
  };
};

export type CreateSignatureRequestCallback = {
  headers: LegalContractWebhookHeaders;
  callbackUrl?: string;
};

export type CreateSignatureRequestOptions = {
  fileToSign: Upload;
  signatureOptions: SignatureOptions;
};

export type ESignDocument = {
  documentId: string;
  documentUrl?: string;
  documentName?: string;
};

export type SignerRole = 'Bloomer' | 'Client';

export type SignerPayload = {
  firstname: string;
  lastname: string;
  email: string;
  role: SignerRole;
  order: number;
  auth_method: 'none';
};

export type ElectronicSignatureRequest = {
  id: string;
  documentId: string;
  status: 'pending' | 'signed' | 'rejected' | 'expired';
  signLink?: string;
};

export type SmartField = {
  [key: string]: string | undefined;
};

export type SmartFields = SmartField[];

export type CancelSignatureReason = 'contractualizationAborted' | 'errorsInDocument' | 'other';

export type CancelSignatureRequestOptions = {
  note?: string;
  reason?: CancelSignatureReason;
};

/**
 * Actor recorded on a cancellation that did not come from a Bloomie but from the provider webhook
 * (someone cancelled the signature request directly in the provider's back-office). Shared so the
 * UI can turn it into a readable label instead of showing the technical value.
 */
export const ESIGN_WEBHOOK_CANCELLATION_ACTOR = 'esign-webhook';

export interface IElectronicSignService {
  createElectronicSignatureRequest: (options: CreateSignatureRequestOptions) => Promise<ElectronicSignatureRequest>;
  downloadFile: (documentId: string) => Promise<Readable>;
  generateDocumentFromTemplate: (
    templateType: TemplateContract,
    newTemplateName: string,
    dataForTemplate: SmartFields,
    externalId: string,
  ) => Promise<ESignDocument>;
  createWebhooks: (
    documentId: string,
    headers: WebhookHeaders,
    eventType: 'documentSigned' | 'signerSigned',
    callbackUrl: string,
  ) => Promise<void>;
  createEmbedInvitation: (documentId: string, signer: SignerPayload) => Promise<string>;
  getEmbedInvitationLink: (documentId: string, signerId: string) => Promise<string>;
  extractWebhookRequestDocumentId: (
    expressRequest: ExpressRequest,
  ) => Promise<{ documentId: string; reason?: string; status: ContractLegalStatus }>;
  /**
   * Idempotent when already cancelled provider-side (replayed jobs converge).
   * Other rejections throw EsignProviderError (`recoverableError` drives retries).
   */
  cancelSignatureRequest: (signatureRequestId: string, options?: CancelSignatureRequestOptions) => Promise<void>;
  serviceName: string;
}
